import type {
  FormData as SubmitFormData,
  FormFields,
  SubmissionAttachmentDetails,
} from '@weave/schema-gen-ts/dist/schemas/forms-digital/weave_digital_forms.pb';
import { getISODate, getTodayISODateTime } from '../../../../helpers/date-helpers';
import { serializePaymentSubmission } from './fields/payment-on-file-field/payment-field.helpers';
import { getSignedAttachmentId } from './fields/pdf-field/pdf-value';
import { getVirtualFieldId } from './helpers';
import { getFieldAttachments } from './media-upload/selectors';
import { MediaAttachment } from './media-upload/types';
import { isImageESign, parseESignValue } from './signature/esign-value';
import { isPersistedMediaId } from './signature/media-id';
import { DynamicFormField, SchemaForm, SchemaFormField } from './types';

// Presentation/action-only field types carry no submittable answer (mirrors the Review screen's skip-list).
// `signature` is intentionally excluded here: its schema/default value isn't submittable either, but its
// paired consent checkbox (`agreesValue`) still needs a `fields[fieldId]` entry - see the loop below.
export const NON_SUBMITTABLE_FIELD_TYPES = new Set(['richText', 'button', 'text-with-button']);

// Field types whose answer is uploaded media. When hidden they're submitted as unanswered and attach nothing,
// so files the patient added before a condition hid the field never reach the submission.
const MEDIA_FIELD_TYPES = new Set(['imageUpload', 'cardCapture', 'eSign', 'pdf']);

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : {};

const asArray = (value: unknown): unknown[] => (Array.isArray(value) ? value : []);

const asStringOrEmpty = (value: unknown): string => (value === null || value === undefined ? '' : String(value));

const serializeChecklistValue = (value: unknown): string =>
  JSON.stringify(asArray(value).filter((item): item is string => typeof item === 'string' && item !== ''));

const MAX_ATTACHMENT_NAME_LENGTH = 30;

/** Attachment name per the submission contract: the label when short enough, else a generic fallback. */
const getAttachmentName = (label: string | undefined, fallback: string, suffix = '') => {
  const base = label && label.length <= MAX_ATTACHMENT_NAME_LENGTH ? label : fallback;
  return `${base}${suffix}`.length <= MAX_ATTACHMENT_NAME_LENGTH ? `${base}${suffix}` : base;
};

const getPdfOriginalAttachmentId = (field: SchemaFormField): string =>
  typeof field.value === 'string' ? field.value : '';

/** FormFields.options is a JSON-string on the wire; schema options may already be a string (pass through) or an array. */
const serializeOptions = (options: SchemaFormField['options']): string | undefined => {
  if (options === undefined) return undefined;
  return typeof options === 'string' ? options : JSON.stringify(options);
};

/** Mirrors `getDefaultValuesObject`'s per-type shapes, in the reverse (submit) direction. */
const serializeFieldValue = (field: SchemaFormField, value: unknown): string => {
  switch (field.meta.type) {
    case 'checklist':
    case 'categorizedChecklist':
    case 'multiselect-dropdown':
      return serializeChecklistValue(value);

    case 'imageUpload':
      // Field value is already the committed media id array (set by ImageUploadField's onChange).
      return JSON.stringify(asArray(value).filter((id): id is string => typeof id === 'string' && id !== ''));

    case 'cardCapture': {
      // Field value is already { front, back } committed media ids (set by CardCaptureSideInput's onChange).
      const { front, back } = asRecord(value);
      return JSON.stringify({ front: asStringOrEmpty(front), back: asStringOrEmpty(back) });
    }

    case 'date':
      return getISODate(asStringOrEmpty(value));

    case 'currentDatePopulator':
      return getTodayISODateTime();

    case 'address': {
      const { address1, address2 } = asRecord(value);
      return JSON.stringify({ address1: asStringOrEmpty(address1), address2: asStringOrEmpty(address2) });
    }

    case 'insuranceVerification':
      return JSON.stringify(value ?? {});

    case 'checkbox':
      // Strings can still arrive from condition update actions; a never-set checkbox stays ''.
      if (value === true || value === 'true') return 'true';
      if (value === false || value === 'false') return 'false';
      return '';

    case 'eSign': {
      // The backend requires the full shape even when unsigned (all keys empty); omitting the value is rejected.
      const { type, data, font_type, timestamp } = parseESignValue(value);
      return JSON.stringify({ type, data, font_type, timestamp });
    }

    case 'pdf':
      return getSignedAttachmentId(value) || getPdfOriginalAttachmentId(field);

    case 'cardOnFile':
    case 'achOnFile':
      // Masked summary only; the payment method reference and load status are never submitted.
      return serializePaymentSubmission(field.meta.type, value);

    default:
      return asStringOrEmpty(value);
  }
};

export interface DynamicFormLiveState {
  formFieldsMap: Record<string, DynamicFormField>;
  attachments: Record<string, MediaAttachment>;
}

export interface BuildSubmitFormsPayloadArgs extends DynamicFormLiveState {
  schemaForms: SchemaForm[];
}

export interface BuildSubmitFormsPayloadResult {
  forms: SubmitFormData[];
  attachmentDetails: SubmissionAttachmentDetails[];
}

/** Builds the `forms`/`attachmentDetails` portion of a SubmitForm request from live store state. */
export const buildSubmitFormsPayload = ({
  schemaForms,
  formFieldsMap,
  attachments,
}: BuildSubmitFormsPayloadArgs): BuildSubmitFormsPayloadResult => {
  const attachmentDetails: SubmissionAttachmentDetails[] = [];
  // The backend rejects a repeated media id, so de-duplicate across the whole packet: an id shared by several
  // forms (a `dataKey` field, a reused signature, the same template PDF) is attached once, to the first form.
  const seenMediaIds = new Set<string>();
  const addAttachmentDetail = (detail: SubmissionAttachmentDetails & { id: string }) => {
    if (seenMediaIds.has(detail.id)) return;
    seenMediaIds.add(detail.id);
    attachmentDetails.push(detail);
  };

  const forms = schemaForms.map((schemaForm): SubmitFormData => {
    const fields: Record<string, FormFields> = {};

    Object.entries(schemaForm.fields).forEach(([fieldId, schemaField]) => {
      if (NON_SUBMITTABLE_FIELD_TYPES.has(schemaField.meta.type)) return;

      const virtualFieldId = getVirtualFieldId(schemaField);
      const liveField = formFieldsMap[virtualFieldId];
      const hidden = liveField?.hidden ?? schemaField.hidden;
      const dropMedia = !!hidden && MEDIA_FIELD_TYPES.has(schemaField.meta.type);
      // Serializing `undefined` yields each media type's unanswered shape (the backend rejects a missing value).
      const value = dropMedia ? undefined : liveField?.value;
      const isSignature = schemaField.meta.type === 'signature';

      const submittedField: FormFields = {
        ...(schemaField as unknown as FormFields),
        hidden,
        options: serializeOptions(schemaField.options),
        // Signature renders only a placeholder in the filling UI, so its schema/default value is never a
        // reviewable answer; only the paired consent checkbox (agreesValue below) carries real data.
        value: isSignature ? '' : serializeFieldValue(schemaField, value),
      };

      if (isSignature) {
        // Consent checkbox paired with the signature field; not a separately submitted field.
        submittedField.agreesValue = Boolean(formFieldsMap[`${virtualFieldId}-checkbox`]?.value);
      }

      fields[fieldId] = submittedField;
      if (dropMedia) return;

      if (schemaField.meta.type === 'imageUpload') {
        getFieldAttachments(attachments, virtualFieldId)
          .filter((attachment) => attachment.status === 'success' && attachment.mediaId)
          .forEach((attachment, index) => {
            addAttachmentDetail({
              id: attachment.mediaId as string,
              name: schemaField.label ? `${schemaField.label} ${index + 1}` : `Attachment ${index + 1}`,
              formId: schemaForm.form.id,
              type: 'image',
            });
          });
      }

      if (schemaField.meta.type === 'eSign') {
        const signature = parseESignValue(value);
        if (isImageESign(signature) && isPersistedMediaId(signature.data)) {
          addAttachmentDetail({
            id: signature.data,
            name: getAttachmentName(schemaField.label, 'E-signature'),
            formId: schemaForm.form.id,
            type: 'image',
          });
        }
      }

      if (schemaField.meta.type === 'pdf') {
        const signedId = getSignedAttachmentId(value);
        const mediaId = signedId || getPdfOriginalAttachmentId(schemaField);
        if (isPersistedMediaId(mediaId)) {
          addAttachmentDetail({
            id: mediaId,
            name: getAttachmentName(schemaField.label, 'PDF attachment', signedId ? ' (signed)' : ''),
            formId: schemaForm.form.id,
            type: 'pdf',
          });
        }
      }

      if (schemaField.meta.type === 'cardCapture') {
        (['front', 'back'] as const).forEach((side) => {
          getFieldAttachments(attachments, `${virtualFieldId}.${side}`)
            .filter((attachment) => attachment.status === 'success' && attachment.mediaId)
            .forEach((attachment) => {
              addAttachmentDetail({
                id: attachment.mediaId as string,
                name: schemaField.label ? `${schemaField.label} (${side})` : `Card ${side}`,
                side,
                formId: schemaForm.form.id,
                type: 'image',
              });
            });
        });
      }
    });

    return {
      form: schemaForm.form,
      sections: schemaForm.sections,
      fields,
      descriptors: schemaForm.descriptors,
    };
  });

  return { forms, attachmentDetails };
};
